// Global Instructions Rule Applied!
import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import { validateTaskRecord, TaskRecord } from '../../../state_manager/data_models/task_record.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

/** Which record a task hangs off — decides the endpoint the task routes are mounted on. */
export type TaskRecordType = 'deal' | 'company' | 'prospect'

export interface TaskFilters {
  include_completed?: boolean
}

/**
 * Fetch tasks for a specific deal
 * @param dealId - The deal ID
 * @param filters - Optional filters for tasks
 * @param accessToken - User's access token from Redux store
 * @returns Promise<TaskRecord[]> Array of validated tasks
 */
export const fetchTasksForDeal = async (
  dealId: string,
  filters?: TaskFilters,
  accessToken?: string | null
): Promise<TaskRecord[]> => {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    // Build query parameters
    const queryParams = new URLSearchParams()
    if (filters?.include_completed !== undefined) {
      queryParams.append('include_completed', filters.include_completed.toString())
    }

    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DEAL_TASKS.LIST, { dealId })
    const urlWithParams = queryParams.toString() ? `${url}?${queryParams.toString()}` : url

    const response = await fetch(urlWithParams, {
      method: API_ROUTES.DEAL_TASKS.LIST.method,
      headers
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      const errorData = await response.json()
      throw new Error(errorData.message || 'Failed to fetch tasks')
    }

    const data = await response.json()

    const tasks = data.data || []

    return validateTaskRecord(tasks, { isArray: true }) as TaskRecord[]
  } catch (error) {
    console.error('Error fetching tasks:', error)
    throw error
  }
}

/**
 * Fetch tasks for a specific account
 * @param accountId - The account ID
 * @param filters - Optional filters for tasks
 * @param accessToken - User's access token from Redux store
 * @returns Promise<TaskRecord[]> Array of validated tasks
 */
export const fetchTasksForAccount = async (
  accountId: string,
  filters?: TaskFilters,
  accessToken?: string | null
): Promise<TaskRecord[]> => {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    // Build query parameters
    const queryParams = new URLSearchParams()
    if (filters?.include_completed !== undefined) {
      queryParams.append('include_completed', filters.include_completed.toString())
    }

    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.ACCOUNT_TASKS.LIST, { accountId })
    const urlWithParams = queryParams.toString() ? `${url}?${queryParams.toString()}` : url

    const response = await fetch(urlWithParams, {
      method: API_ROUTES.ACCOUNT_TASKS.LIST.method,
      headers
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      const errorData = await response.json()
      throw new Error(errorData.message || 'Failed to fetch tasks')
    }

    const data = await response.json()

    const tasks = data.data || []

    return validateTaskRecord(tasks, { isArray: true }) as TaskRecord[]
  } catch (error) {
    console.error('Error fetching tasks:', error)
    throw error
  }
}

/**
 * Fetch tasks for a specific prospect
 * @param prospectId - The prospect ID
 * @param filters - Optional filters for tasks
 * @param accessToken - User's access token from Redux store
 * @returns Promise<TaskRecord[]> Array of validated tasks
 */
export const fetchTasksForProspect = async (
  prospectId: string,
  filters?: TaskFilters,
  accessToken?: string | null
): Promise<TaskRecord[]> => {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    // Build query parameters
    const queryParams = new URLSearchParams()
    if (filters?.include_completed !== undefined) {
      queryParams.append('include_completed', filters.include_completed.toString())
    }

    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.PROSPECT_TASKS.LIST, { prospectId })
    const urlWithParams = queryParams.toString() ? `${url}?${queryParams.toString()}` : url

    const response = await fetch(urlWithParams, {
      method: API_ROUTES.PROSPECT_TASKS.LIST.method,
      headers
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      const errorData = await response.json()
      throw new Error(errorData.message || 'Failed to fetch tasks')
    }

    const data = await response.json()

    const tasks = data.data || []

    return validateTaskRecord(tasks, { isArray: true }) as TaskRecord[]
  } catch (error) {
    console.error('Error fetching tasks:', error)
    throw error
  }
}

/**
 * Create a new task
 * @param recordId - The record ID (deal ID, account ID, or prospect ID)
 * @param taskData - The task data
 * @param accessToken - User's access token from Redux store
 * @param recordType - The record type ('deal', 'company', or 'prospect')
 * @param accountId - Account ID (required for deal/prospect to link task to account)
 * @returns Promise<Object> Created task
 */
export const createTask = async (
  recordId: string,
  taskData: unknown,
  accessToken?: string | null,
  recordType: TaskRecordType = 'deal',
  accountId?: string | null
): Promise<TaskRecord> => {
  try {
    // Validate input before sending (taskData only, recordId is in task_deals__deal_id or task_accounts__account_id field)
    validateTaskRecord(taskData)

    // Ensure taskData has the correct relationship field based on recordType
    const taskDataWithRelationship = { ...(taskData as Record<string, any>) }
    if (recordType === 'company') {
      taskDataWithRelationship['task_accounts__account_id'] = recordId
      delete taskDataWithRelationship['task_deals__deal_id']
      delete taskDataWithRelationship['task_prospects__prospect_id']
    } else if (recordType === 'prospect') {
      taskDataWithRelationship['task_prospects__prospect_id'] = recordId
      delete taskDataWithRelationship['task_deals__deal_id']
      if (accountId) {
        taskDataWithRelationship['task_accounts__account_id'] = accountId
      }
    } else {
      taskDataWithRelationship['task_deals__deal_id'] = recordId
      delete taskDataWithRelationship['task_prospects__prospect_id']
      if (accountId) {
        taskDataWithRelationship['task_accounts__account_id'] = accountId
      }
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.TASKS.CREATE), {
      method: API_ROUTES.TASKS.CREATE.method,
      headers,
      body: JSON.stringify(taskDataWithRelationship)
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      const errorData = await response.json()
      throw new Error(errorData.message || 'Failed to create task')
    }

    const data = await response.json()

    if (data.success) {
      // Backend returns TaskRecord format directly, just validate it
      return validateTaskRecord(data.data)
    } else {
      throw new Error(data.message || 'Failed to create task')
    }
  } catch (error) {
    console.error('Error creating task:', error)
    throw error
  }
}

/**
 * Update an existing task
 * @param recordId - The record ID (deal ID, account ID, or prospect ID)
 * @param taskId - The task ID
 * @param taskData - The updated task data
 * @param accessToken - User's access token from Redux store
 * @param recordType - The record type ('deal', 'company', or 'prospect')
 * @returns Promise<Object> Updated task
 */
export const updateTask = async (
  recordId: string,
  taskId: string,
  taskData: unknown,
  accessToken?: string | null,
  recordType: TaskRecordType = 'deal'
): Promise<TaskRecord> => {
  try {
    // Validate input before sending
    validateTaskRecord(taskData)

    const taskDataWithRelationship = { ...(taskData as Record<string, any>) }

    // Only add relationship fields if they're explicitly present in taskData
    // This prevents sending relationship fields when just updating completion or other fields
    const hasDealId = taskDataWithRelationship.hasOwnProperty('task_deals__deal_id')
    const hasAccountId = taskDataWithRelationship.hasOwnProperty('task_accounts__account_id')
    const hasProspectId = taskDataWithRelationship.hasOwnProperty('task_prospects__prospect_id')

    if (hasAccountId && recordType === 'company') {
      // Relationship field already present, ensure it's correct
      taskDataWithRelationship['task_accounts__account_id'] = recordId
      delete taskDataWithRelationship['task_deals__deal_id']
      delete taskDataWithRelationship['task_prospects__prospect_id']
    } else if (hasProspectId && recordType === 'prospect') {
      // Relationship field already present, ensure it's correct
      taskDataWithRelationship['task_prospects__prospect_id'] = recordId
      delete taskDataWithRelationship['task_deals__deal_id']
      delete taskDataWithRelationship['task_accounts__account_id']
    } else if (hasDealId && recordType === 'deal') {
      // Relationship field already present, ensure it's correct
      taskDataWithRelationship['task_deals__deal_id'] = recordId
      delete taskDataWithRelationship['task_accounts__account_id']
      delete taskDataWithRelationship['task_prospects__prospect_id']
    } else {
      // No relationship fields in taskData - don't add them
      // This prevents triggering relationship update logic when just updating task fields
      delete taskDataWithRelationship['task_deals__deal_id']
      delete taskDataWithRelationship['task_accounts__account_id']
      delete taskDataWithRelationship['task_prospects__prospect_id']
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.TASKS.UPDATE, { id: taskId }), {
      method: API_ROUTES.TASKS.UPDATE.method,
      headers,
      body: JSON.stringify(taskDataWithRelationship)
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      const errorData = await response.json()
      throw new Error(errorData.message || 'Failed to update task')
    }

    const data = await response.json()

    if (data.success) {
      // Backend returns TaskRecord format directly, just validate it
      return validateTaskRecord(data.data)
    } else {
      throw new Error(data.message || 'Failed to update task')
    }
  } catch (error) {
    console.error('Error updating task:', error)
    throw error
  }
}

/**
 * Delete a task
 * @param dealId - The deal ID
 * @param taskId - The task ID
 * @param accessToken - User's access token from Redux store
 * @returns Promise<void>
 */
export const deleteTask = async (dealId: string, taskId: string, accessToken?: string | null): Promise<void> => {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.TASKS.DELETE, { id: taskId }), {
      method: API_ROUTES.TASKS.DELETE.method,
      headers
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      const errorData = await response.json()
      throw new Error(errorData.message || 'Failed to delete task')
    }

    const data = await response.json()
    if (data && data.success === false) {
      throw new Error(data.message || data.error || 'Failed to delete task')
    }
  } catch (error) {
    console.error('Error deleting task:', error)
    throw error
  }
}

/**
 * Fetch task links (accounts, deals, prospects)
 * @param taskId - The task ID
 * @param accessToken - User's access token from Redux store
 * @returns Promise with account_ids, deal_ids, prospect_ids arrays (objects with id and label)
 */
export const fetchTaskLinks = async (
  taskId: string,
  accessToken?: string | null
): Promise<{
  account_ids: Array<{ id: string; label: string }>
  deal_ids: Array<{ id: string; label: string }>
  prospect_ids: Array<{ id: string; label: string }>
}> => {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.TASKS.LINKS, { taskId }), {
      method: API_ROUTES.TASKS.LINKS.method,
      headers
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      const errorData = await response.json()
      throw new Error(errorData.message || 'Failed to fetch task links')
    }

    const data = await response.json()

    return {
      account_ids: data.data?.account_ids || [],
      deal_ids: data.data?.deal_ids || [],
      prospect_ids: data.data?.prospect_ids || []
    }
  } catch (error) {
    console.error('Error fetching task links:', error)
    throw error
  }
}

/**
 * Process task links (update accounts, deals, prospects)
 * @param data - Object with task_id, account_ids, deal_ids, prospect_ids
 * @param accessToken - User's access token from Redux store
 * @returns Promise<void>
 */
export const processTaskLinks = async (
  data: {
    task_id: string
    account_ids: string[]
    deal_ids: string[]
    prospect_ids: string[]
  },
  accessToken?: string | null
): Promise<void> => {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.TASKS.PROCESS), {
      method: API_ROUTES.TASKS.PROCESS.method,
      headers,
      body: JSON.stringify(data)
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      const errorData = await response.json()
      throw new Error(errorData.message || 'Failed to process task links')
    }

    const responseData = await response.json()

    const isSuccess = responseData.success === true || responseData.data?.success === true
    if (!isSuccess) {
      throw new Error(responseData.message || responseData.data?.message || 'Failed to process task links')
    }
  } catch (error) {
    console.error('Error processing task links:', error)
    throw error
  }
}
